import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDongDisplayName } from '../utils/dong';

interface Building {
  id: number;
  dongNumber: number;
  dongName?: string | null;
  buildingType?: string;
  accessibility?: string;
  accessNotes?: string;
  entranceCount?: number;
  hasElevator?: boolean;
  hasIntercom?: boolean | null;
  maxFloor: number;
  unitPattern: any;
  complex: {
    id: number;
    name: string;
    code: number;
  };
}

interface ClassificationHistory {
  id: number;
  changes: {
    buildingType: { from: string | null; to: string | null };
    accessibility: { from: string | null; to: string | null };
    accessNotes: { from: string | null; to: string | null };
    entranceCount: { from: number | null; to: number | null };
    hasElevator: { from: boolean | null; to: boolean | null };
    hasIntercom?: { from: boolean | null; to: boolean | null };
  };
  metadata: {
    changeReason: string | null;
    changedBy: string | null;
    changeNotes: string | null;
    createdAt: string;
  };
}

interface ClassificationHistoryResponse {
  message: string;
  data: {
    building: {
      id: number;
      dongNumber: number;
      complex: { name: string; code: number };
    };
    history: ClassificationHistory[];
    totalRecords: number;
  };
}

interface BuildingClassificationEditModalProps {
  building: Building;
  isOpen: boolean;
  onClose: () => void;
}

interface UpdateData {
  buildingType?: string | undefined;
  accessibility?: string | undefined;
  accessNotes?: string | undefined;
  entranceCount?: number | undefined;
  hasElevator?: boolean | undefined;
  hasIntercom?: boolean | undefined;
}

const fetchClassificationHistory = async (
  buildingId: number
): Promise<ClassificationHistoryResponse> => {
  const response = await fetch(
    `/api/buildings/${buildingId}/classification/history`
  );
  if (!response.ok) {
    throw new Error('Failed to fetch classification history');
  }
  return response.json();
};

const updateBuildingClassification = async (
  buildingId: number,
  data: UpdateData
) => {
  const response = await fetch(`/api/buildings/${buildingId}/classification`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(
      errorData.message || 'Failed to update building classification'
    );
  }

  return response.json();
};

const BuildingClassificationEditModal: React.FC<
  BuildingClassificationEditModalProps
> = ({ building, isOpen, onClose }) => {
  const [formData, setFormData] = useState<UpdateData>({
    buildingType: building.buildingType || '',
    accessibility: building.accessibility || '',
    accessNotes: building.accessNotes || '',
    entranceCount: building.entranceCount || 1,
    hasElevator: building.hasElevator || false,
    hasIntercom: building.hasIntercom || false,
  });
  const [showHistory, setShowHistory] = useState(false);

  const queryClient = useQueryClient();

  // Reset form data when building changes
  useEffect(() => {
    setFormData({
      buildingType: building.buildingType || '',
      accessibility: building.accessibility || '',
      accessNotes: building.accessNotes || '',
      entranceCount: building.entranceCount || 1,
      hasElevator: building.hasElevator || false,
      hasIntercom: building.hasIntercom || false,
    });
  }, [building]);

  // Fetch classification history
  const { data: historyData, isLoading: historyLoading } = useQuery({
    queryKey: ['buildingHistory', building.id],
    queryFn: () => fetchClassificationHistory(building.id),
    enabled: isOpen && showHistory,
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: UpdateData) =>
      updateBuildingClassification(building.id, data),
    onSuccess: () => {
      // Invalidate and refetch building data
      queryClient.invalidateQueries({ queryKey: ['buildings'] });
      queryClient.invalidateQueries({
        queryKey: ['buildingHistory', building.id],
      });
      onClose();
    },
    onError: (error: Error) => {
      alert(`업데이트 실패: ${error.message}`);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Only send changed fields
    const changedData: UpdateData = {};

    if (formData.buildingType !== building.buildingType) {
      changedData.buildingType = formData.buildingType;
    }
    if (formData.accessibility !== building.accessibility) {
      changedData.accessibility = formData.accessibility;
    }
    if (formData.accessNotes !== building.accessNotes) {
      changedData.accessNotes = formData.accessNotes;
    }
    if (formData.entranceCount !== building.entranceCount) {
      changedData.entranceCount = formData.entranceCount;
    }
    if ((formData.hasElevator ?? false) !== (building.hasElevator ?? false)) {
      changedData.hasElevator = formData.hasElevator;
    }
    if ((formData.hasIntercom ?? false) !== (building.hasIntercom ?? false)) {
      changedData.hasIntercom = formData.hasIntercom;
    }

    // Only update if there are changes
    if (Object.keys(changedData).length > 0) {
      updateMutation.mutate(changedData);
    } else {
      onClose();
    }
  };

  const getBuildingTypeLabel = (type: string) => {
    switch (type) {
      case 'corridor':
        return '복도식';
      case 'staircase':
        return '계단식';
      case 'general':
        return '일반식';
      default:
        return type;
    }
  };

  const getAccessibilityLabel = (accessibility: string) => {
    switch (accessibility) {
      case 'accessible':
        return '접근 가능';
      case 'restricted':
        return '제한적 접근';
      case 'inaccessible':
        return '출입 불가';
      default:
        return accessibility;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ko-KR');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold">
            {formatDongDisplayName(building.dongNumber, building.dongName)} 분류 편집
            <span className="text-sm text-gray-500 ml-2">
              ({building.complex.name})
            </span>
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-xl"
          >
            ✕
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Edit Form */}
          <div>
            <h4 className="text-lg font-medium mb-4">분류 정보 수정</h4>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Building Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  동 유형
                </label>
                <select
                  value={formData.buildingType}
                  onChange={e =>
                    setFormData({ ...formData, buildingType: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">선택하세요</option>
                  <option value="corridor">복도식</option>
                  <option value="staircase">계단식</option>
                  <option value="general">일반식</option>
                </select>
              </div>

              {/* Accessibility */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  접근성
                </label>
                <select
                  value={formData.accessibility}
                  onChange={e =>
                    setFormData({ ...formData, accessibility: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">선택하세요</option>
                  <option value="accessible">접근 가능</option>
                  <option value="restricted">제한적 접근</option>
                  <option value="inaccessible">출입 불가</option>
                </select>
              </div>

              {/* Entrance Count */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  입구 수
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.entranceCount}
                  onChange={e =>
                    setFormData({
                      ...formData,
                      entranceCount: parseInt(e.target.value),
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Has Elevator */}
              <div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.hasElevator}
                    onChange={e =>
                      setFormData({
                        ...formData,
                        hasElevator: e.target.checked,
                      })
                    }
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    엘리베이터 있음
                  </span>
                </label>
              </div>

              {/* Has Intercom */}
              <div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.hasIntercom}
                    onChange={e =>
                      setFormData({
                        ...formData,
                        hasIntercom: e.target.checked,
                      })
                    }
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    인터폰 있음
                  </span>
                </label>
              </div>

              {/* Access Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  특이사항
                </label>
                <textarea
                  value={formData.accessNotes}
                  onChange={e =>
                    setFormData({ ...formData, accessNotes: e.target.value })
                  }
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="접근 관련 특이사항을 입력하세요..."
                />
              </div>

              {/* Submit Buttons */}
              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  disabled={updateMutation.isPending}
                  className="flex-1 bg-blue-500 hover:bg-blue-700 disabled:bg-blue-300 text-white py-2 px-4 rounded"
                >
                  {updateMutation.isPending ? '저장 중...' : '저장'}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 bg-gray-500 hover:bg-gray-700 text-white py-2 px-4 rounded"
                >
                  취소
                </button>
              </div>
            </form>
          </div>

          {/* History Section */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-medium">분류 이력</h4>
              <button
                onClick={() => setShowHistory(!showHistory)}
                className="text-blue-500 hover:text-blue-700 text-sm"
              >
                {showHistory ? '숨기기' : '보기'}
              </button>
            </div>

            {showHistory && (
              <div className="border rounded-lg p-4 max-h-96 overflow-y-auto">
                {historyLoading ? (
                  <div className="text-center py-4 text-gray-500">
                    이력을 불러오는 중...
                  </div>
                ) : historyData?.data.history.length === 0 ? (
                  <div className="text-center py-4 text-gray-500">
                    변경 이력이 없습니다.
                  </div>
                ) : (
                  <div className="space-y-4">
                    {historyData?.data.history.map(record => (
                      <div
                        key={record.id}
                        className="border-b pb-3 last:border-b-0"
                      >
                        <div className="text-xs text-gray-500 mb-2">
                          {formatDate(record.metadata.createdAt)}
                          {record.metadata.changeReason && (
                            <span className="ml-2 px-2 py-1 bg-gray-100 rounded text-xs">
                              {record.metadata.changeReason}
                            </span>
                          )}
                        </div>

                        <div className="space-y-1 text-sm">
                          {record.changes.buildingType.from !==
                            record.changes.buildingType.to && (
                            <div>
                              <span className="font-medium">동 유형:</span>
                              <span className="text-red-600 ml-1">
                                {record.changes.buildingType.from
                                  ? getBuildingTypeLabel(
                                      record.changes.buildingType.from
                                    )
                                  : '없음'}
                              </span>
                              <span className="mx-1">→</span>
                              <span className="text-green-600">
                                {record.changes.buildingType.to
                                  ? getBuildingTypeLabel(
                                      record.changes.buildingType.to
                                    )
                                  : '없음'}
                              </span>
                            </div>
                          )}

                          {record.changes.accessibility.from !==
                            record.changes.accessibility.to && (
                            <div>
                              <span className="font-medium">접근성:</span>
                              <span className="text-red-600 ml-1">
                                {record.changes.accessibility.from
                                  ? getAccessibilityLabel(
                                      record.changes.accessibility.from
                                    )
                                  : '없음'}
                              </span>
                              <span className="mx-1">→</span>
                              <span className="text-green-600">
                                {record.changes.accessibility.to
                                  ? getAccessibilityLabel(
                                      record.changes.accessibility.to
                                    )
                                  : '없음'}
                              </span>
                            </div>
                          )}

                          {record.changes.entranceCount.from !==
                            record.changes.entranceCount.to && (
                            <div>
                              <span className="font-medium">입구 수:</span>
                              <span className="text-red-600 ml-1">
                                {record.changes.entranceCount.from || '없음'}
                              </span>
                              <span className="mx-1">→</span>
                              <span className="text-green-600">
                                {record.changes.entranceCount.to || '없음'}
                              </span>
                            </div>
                          )}

                          {record.changes.hasElevator.from !==
                            record.changes.hasElevator.to && (
                            <div>
                              <span className="font-medium">엘리베이터:</span>
                              <span className="text-red-600 ml-1">
                                {record.changes.hasElevator.from
                                  ? '있음'
                                  : '없음'}
                              </span>
                              <span className="mx-1">→</span>
                              <span className="text-green-600">
                                {record.changes.hasElevator.to
                                  ? '있음'
                                  : '없음'}
                              </span>
                            </div>
                          )}

                          {record.changes.hasIntercom &&
                            record.changes.hasIntercom.from !==
                              record.changes.hasIntercom.to && (
                            <div>
                              <span className="font-medium">인터폰:</span>
                              <span className="text-red-600 ml-1">
                                {record.changes.hasIntercom.from ? '있음' : '없음'}
                              </span>
                              <span className="mx-1">→</span>
                              <span className="text-green-600">
                                {record.changes.hasIntercom.to ? '있음' : '없음'}
                              </span>
                            </div>
                          )}

                          {record.changes.accessNotes.from !==
                            record.changes.accessNotes.to && (
                            <div>
                              <span className="font-medium">특이사항:</span>
                              <div className="ml-4 text-xs">
                                <div className="text-red-600">
                                  이전:{' '}
                                  {record.changes.accessNotes.from || '없음'}
                                </div>
                                <div className="text-green-600">
                                  현재:{' '}
                                  {record.changes.accessNotes.to || '없음'}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        {record.metadata.changeNotes && (
                          <div className="text-xs text-gray-600 mt-2 italic">
                            {record.metadata.changeNotes}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BuildingClassificationEditModal;
